import { create } from 'zustand'

/**
 * Whether the server has told us, mid-session, that this account must accept the NDA.
 *
 * Deliberately NOT persisted, and deliberately not a record of having accepted. The server is
 * the only authority on acceptance — it re-checks on every request — so anything stored here is
 * a convenience for rendering, never a fact. Persisting it would create the two failure modes
 * worth avoiding: a stale "accepted" that lets a locked-out account render an app whose every
 * request 403s, and a stale "blocked" that shows the agreement to somebody who already signed it.
 *
 * `blocked` is set by the API client's 403 handler and cleared once fresh server data says the
 * user is through. It only ever makes the gate *stricter* than the last fetched status; the gate
 * still asks the server before letting anyone past.
 */
interface NdaState {
  /** The server refused a request with `nda_required` and we have not yet confirmed otherwise. */
  blocked: boolean
  /** The version the server said was outstanding, if it told us. Informational. */
  version: number | null

  setBlocked: (version: number | null) => void
  clearBlocked: () => void
}

export const useNdaStore = create<NdaState>(set => ({
  blocked: false,
  version: null,

  setBlocked: version => set({ blocked: true, version }),
  clearBlocked: () => set({ blocked: false, version: null }),
}))
